import React, { useState, useRef } from 'react';
import { ArrowDownTrayIcon, ArrowUpTrayIcon, TrashIcon, Cog6ToothIcon } from '@heroicons/react/24/outline';
import { useTranslationStore } from '../store/translationStore';
import toast from 'react-hot-toast';

function DataManagement() {
  const [showMenu, setShowMenu] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { exportData, importData, clearHistory, history } = useTranslationStore();

  const handleExport = async () => {
    try {
      await exportData();
      toast.success('Data exported successfully');
      setShowMenu(false);
    } catch (error) {
      toast.error('Failed to export data');
    }
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsImporting(true);
    try {
      const result = await importData(file);
      toast.success(`Imported ${result.imported} new, merged ${result.merged} existing`);
      setShowMenu(false);
    } catch (error) {
      toast.error('Failed to import data');
    } finally {
      setIsImporting(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleClearData = async () => {
    try {
      await clearHistory();
      toast.success('All data cleared');
      setShowClearConfirm(false);
      setShowMenu(false);
    } catch (error) {
      toast.error('Failed to clear data');
    }
  };

  return (
    <div className="relative">
      <button
        onClick={() => setShowMenu(!showMenu)}
        className="flex items-center space-x-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 transition-colors rounded-lg hover:bg-gray-100"
      >
        <Cog6ToothIcon className="h-5 w-5" />
      </button>

      {showMenu && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-10"
            onClick={() => {
              setShowMenu(false);
              setShowClearConfirm(false);
            }}
          />

          {/* Dropdown Menu */}
          <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 z-20">
            <div className="py-2">
              <div className="px-4 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider">
                Data Management
              </div>

              <button
                onClick={handleExport}
                disabled={history.length === 0}
                className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ArrowDownTrayIcon className="h-4 w-4 mr-3" />
                Export Data
                {history.length > 0 && (
                  <span className="ml-auto text-xs text-gray-400">
                    {history.length} items
                  </span>
                )}
              </button>

              <button
                onClick={handleImportClick}
                disabled={isImporting}
                className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50"
              >
                <ArrowUpTrayIcon className="h-4 w-4 mr-3" />
                {isImporting ? 'Importing...' : 'Import Data'}
              </button>

              <div className="border-t border-gray-100 my-2" />

              {!showClearConfirm ? (
                <button
                  onClick={() => setShowClearConfirm(true)}
                  disabled={history.length === 0}
                  className="w-full flex items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <TrashIcon className="h-4 w-4 mr-3" />
                  Clear All Data
                </button>
              ) : (
                <div className="px-4 py-3 bg-red-50">
                  <p className="text-sm text-red-700 mb-3">
                    Delete all {history.length} translations? This cannot be undone.
                  </p>
                  <div className="flex space-x-2">
                    <button
                      onClick={handleClearData}
                      className="flex-1 px-3 py-1.5 bg-red-600 text-white text-sm rounded hover:bg-red-700"
                    >
                      Delete
                    </button>
                    <button
                      onClick={() => setShowClearConfirm(false)}
                      className="flex-1 px-3 py-1.5 bg-white text-gray-700 text-sm rounded border border-gray-300 hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".json"
        onChange={handleFileChange}
        className="hidden"
      />
    </div>
  );
}

export default DataManagement;
